import mixpanelWeb from 'mixpanel-browser';

import { ApolloClient } from '@apollo/client';

import config from 'lane-shared/config';
import { debounce } from './debounce';
import { AnalyticsHelper } from 'lane-shared/helpers';
import { ANALYTIC_KEYS, AnalyticKey } from 'constants-analytics';

import history from './history';
import {
  AnalyticsDataType,
  AnalyticsInterface,
} from 'packages/lane-shared/types/Analytics';

let analytics: AnalyticsHelper;
let lastLocation: any;
let mixpanelInitialized = false;
let historyListenerSetup = false;

/**
 * Initialize Mixpanel. Idempotent - safe to call multiple times.
 * Called by Klaro consent callback when user grants analytics consent.
 */
export function initializeMixpanel(): void {
  if (mixpanelInitialized || typeof window === 'undefined') {
    return;
  }

  mixpanelWeb.init(config.mixpanel.token);
  // Clear any previous opt-out state from revoked consent
  mixpanelWeb.opt_in_tracking();
  mixpanelInitialized = true;
}

/**
 * Shutdown Mixpanel when consent is revoked.
 */
export function shutdownMixpanel(): void {
  if (!mixpanelInitialized) {
    return;
  }

  try {
    mixpanelWeb.reset();
    mixpanelWeb.opt_out_tracking();
  } catch {
    // Ignore 
  }

  mixpanelInitialized = false;
}

// Check if Mixpanel has been initialized (consent granted).
export function isMixpanelInitialized(): boolean {
  return mixpanelInitialized;
}

export function analyticsHelper(client: ApolloClient<any>): AnalyticsInterface {
  if (!analytics) {
    analytics = new AnalyticsHelper({ client });

    track(ANALYTIC_KEYS.ANALYTIC_PATH, {
      path: history.location.pathname,
      category: 'enter',
      value: (history.location as any).query,
    });

    lastLocation = history.location;
  }

  // Setup history listener once (independent of Mixpanel state)
  if (!historyListenerSetup) {
    historyListenerSetup = true;

    history.listen(
      // TODO: Fix the type "any"
      debounce((location: any) => {
        if (lastLocation) {
          track(ANALYTIC_KEYS.ANALYTIC_PATH, {
            path: lastLocation.pathname,
            category: 'leave',
            value: lastLocation.query,
          });
        }

        track(ANALYTIC_KEYS.ANALYTIC_PATH, {
          path: location.pathname,
          category: 'enter',
          value: (location as any).query,
        });

        lastLocation = location;
      }, 240)
    );
  }

  function track(event: AnalyticKey, data: AnalyticsDataType) {
    analytics.track(event, { ...data });

    // Only call Mixpanel if initialized (consent granted)
    if (mixpanelInitialized) {
      mixpanelWeb.track(event, { ...data });
    }
  }

  function trackMixpanel(event: AnalyticKey, data: AnalyticsDataType) {
    if (mixpanelInitialized) {
      mixpanelWeb.track(event, { ...data });
    }
  }

  function setMixpanelSessionId(sessionId: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.register({ sessionId });
    }
  }

  function setChannel(channelId: string, channelName: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.register({ channelId, channelName });
    }
  }

  function identify(userId: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.identify(userId);
    }
  }

  function update(props: Record<string, unknown>) {
    if (mixpanelInitialized) {
      mixpanelWeb.people.set(props);
    }
  }

  return {
    track,
    trackMixpanel,
    identify,
    update,
    setMixpanelSessionId,
    setChannel,
    onNavigationStateChange: () => {},
  };
}
